import React, { isValidElement, cloneElement, useMemo } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LinkifiedPlainText } from 'features/agent-analytics/components/payloadEntityLinks';
import { tableHeaderCellClass, tableDataCellClass } from 'features/activity/styles/dataTableStyles';

// Markdown hands us bare <tr>s, so the header and body rows are styled from
// their section: header hairline in border-default, body hairlines in
// border-subtle with none under the last row (tableHeaderRowClass/tableRowClass).
const MD_THEAD_CLASS = '[&>tr]:h-8 [&>tr]:border-b [&>tr]:border-[color:var(--border-default)]';
const MD_TBODY_CLASS = '[&>tr]:h-8 [&>tr]:border-b [&>tr]:border-[color:var(--border-subtle)] [&>tr:last-child]:border-b-0';

function markdownCodeChildrenToString(children) {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) {
    return children.map((c) => (typeof c === 'string' ? c : String(c))).join('');
  }
  return String(children ?? '');
}

function linkifyChildren(children, { onRunClick, onSpanClick }) {
  if (!onRunClick && !onSpanClick) return children;
  return React.Children.map(children, (child, i) => {
    if (child == null || child === false) return child;
    if (typeof child === 'string') {
      return (
        <LinkifiedPlainText
          key={i}
          text={child}
          onRunClick={onRunClick}
          onSpanClick={onSpanClick}
        />
      );
    }
    if (Array.isArray(child)) {
      return <React.Fragment key={i}>{linkifyChildren(child, { onRunClick, onSpanClick })}</React.Fragment>;
    }
    if (isValidElement(child) && child.props?.children != null) {
      return cloneElement(
        child,
        { key: child.key ?? i },
        linkifyChildren(child.props.children, { onRunClick, onSpanClick }),
      );
    }
    return child;
  });
}

/**
 * Renders assistant output (UTF-8 / Markdown + GFM). Used in trace chat and message detail.
 */
export default function AgentMarkdownContent({
  children,
  className = '',
  onRunClick,
  onSpanClick,
}) {
  const text = children == null ? '' : String(children);
  const linkifyProps = { onRunClick, onSpanClick };

  const markdownComponents = useMemo(() => ({
    p: ({ children: pChildren, ...props }) => (
      <p className="mb-2 last:mb-0 leading-relaxed" {...props}>
        {linkifyChildren(pChildren, linkifyProps)}
      </p>
    ),
    h1: (props) => <h1 className="mb-2 mt-3 text-base font-semibold first:mt-0" {...props} />,
    h2: (props) => <h2 className="mb-2 mt-3 text-sm font-semibold first:mt-0" {...props} />,
    h3: (props) => <h3 className="mb-1.5 mt-2 text-sm font-semibold first:mt-0" {...props} />,
    ul: (props) => <ul className="my-2 list-disc space-y-1 pl-5" {...props} />,
    ol: (props) => <ol className="my-2 list-decimal space-y-1 pl-5" {...props} />,
    li: ({ children: liChildren, ...props }) => (
      <li className="leading-relaxed" {...props}>
        {linkifyChildren(liChildren, linkifyProps)}
      </li>
    ),
    blockquote: (props) => (
      <blockquote
        className="my-2 border-l-2 border-[color:var(--border-default)] pl-3 italic text-[color:var(--text-secondary)]"
        {...props}
      />
    ),
    a: ({ href, children, ...props }) => {
      // Entity links the analyst wrote (`trodo://entity/<kind>/<id>`): open the
      // run/span drawer this view already owns rather than a new browser tab.
      const m = typeof href === 'string' && /^trodo:\/\/entity\/([a-z_]+)\/(.+)$/i.exec(href);
      if (m) {
        const kind = m[1];
        const id = decodeURIComponent(m[2]);
        const open = kind === 'span' ? onSpanClick : kind === 'run' ? onRunClick : null;
        return (
          <a
            {...props}
            href={href}
            title={`${kind}: ${id}`}
            className="rounded-[5px] border border-[color:var(--trodo-primary)]/30 bg-[color:var(--trodo-primary)]/10 px-1.5 py-px font-mono text-[0.84em] font-medium text-[color:var(--trodo-primary)] hover:bg-[color:var(--trodo-primary)]/20"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (open) open(id);
              else if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('trodo:chat:openEntity', { detail: { kind, id } }));
              }
            }}
          >
            {children}
          </a>
        );
      }
      return (
        <a
          className="font-medium text-[color:var(--trodo-primary)] underline underline-offset-2 hover:opacity-90"
          target="_blank"
          rel="noopener noreferrer"
          href={href}
          {...props}
        >
          {children}
        </a>
      );
    },
    code: ({ className: codeClass, children: codeChildren, ...props }) => {
      const raw = markdownCodeChildrenToString(codeChildren);
      const isBlock = /language-/.test(codeClass || '') || raw.includes('\n');
      if (!isBlock) {
        return (
          <code
            className="rounded bg-surface-inverse/[0.06] px-1 py-0.5 font-mono text-[0.9em] text-[color:var(--text-primary)]"
            {...props}
          >
            {codeChildren}
          </code>
        );
      }
      const lang = (codeClass || '').replace(/^language-/, '');
      return (
        <code className={`block whitespace-pre-wrap ${codeClass || ''}`} {...props}>
          {codeChildren}
        </code>
      );
    },
    pre: (props) => (
      <pre
        className="agent-md-pre my-2 overflow-x-auto rounded-lg bg-surface-hover p-3 font-mono text-xs leading-snug"
        {...props}
      />
    ),
    // Same table as Traces: bordered card, hairline rows, micro-label header
    // with no fill. Cells may wrap, so py-1 keeps a multi-line cell off the
    // hairlines; a one-line row still sits at h-8.
    table: (props) => (
      <div className="my-2 overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-left" {...props} />
        </div>
      </div>
    ),
    thead: (props) => <thead className={MD_THEAD_CLASS} {...props} />,
    tbody: (props) => <tbody className={MD_TBODY_CLASS} {...props} />,
    th: (props) => (
      <th className={tableHeaderCellClass} {...props} />
    ),
    td: ({ children: tdChildren, ...props }) => (
      <td className={`${tableDataCellClass} py-1`} {...props}>
        {linkifyChildren(tdChildren, linkifyProps)}
      </td>
    ),
    hr: (props) => <hr className="my-3 border-[color:var(--border-default)]" {...props} />,
    strong: (props) => <strong className="font-semibold" {...props} />,
  }), [onRunClick, onSpanClick]);

  return (
    <div
      className={`agent-md text-[color:var(--text-primary)] [&_.agent-md-pre]:overflow-x-auto ${className}`}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
        // react-markdown's default transform allows only http/https/mailto/tel,
        // so `trodo://entity/...` hrefs were stripped to "" and entity links
        // rendered as inert text. Allow ours, delegate the rest (keeps
        // javascript: blocked).
        urlTransform={(url) => (typeof url === 'string' && url.startsWith('trodo://entity/')
          ? url : defaultUrlTransform(url))}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
